import { useState, useRef, useEffect } from 'react'
import { assetPath } from '@/utils/assetPath'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown, User } from 'lucide-react'
import { User as FirebaseUser } from 'firebase/auth'
import { signOut } from '@/auth/firebase'
import { ROUTES } from '@/constants/routes'
import { useProtectedAction } from '@/hooks/useProtectedAction'

export const UserMenu = ({
    user,
    userId,
    profileImage,
    navigate,
    onSettingsClick,
}: {
    user: FirebaseUser | null
    userId: string | undefined
    profileImage: string
    navigate: ({ to }: { to: string }) => void
    onSettingsClick?: () => void
}) => {
    const [menuOpen, setMenuOpen] = useState(false)
    const menuRef = useRef<HTMLDivElement>(null)
    const [isLoggingOut, setIsLoggingOut] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const { withAuth } = useProtectedAction()

    const handleLogout = async () => {
        try {
            setIsLoggingOut(true)
            setError(null)
            await signOut()
            // Redirect to home page after successful logout
            navigate({ to: '/onboarding' })
        } catch (err) {
            console.error('Error signing out:', err)
            setError('Failed to sign out. Please try again.')
        } finally {
            setIsLoggingOut(false)
        }
    }

    useEffect(() => {
        if (!menuOpen) return
        function handleClick(e: MouseEvent) {
            if (
                menuRef.current &&
                !menuRef.current.contains(e.target as Node)
            ) {
                setMenuOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClick)
        return () => document.removeEventListener('mousedown', handleClick)
    }, [menuOpen])

    return (
        <div
            className="relative flex cursor-pointer items-center gap-4"
            ref={menuRef}
        >
            <div
                onClick={withAuth(() =>
                    navigate({ to: `/profiles/${userId}` })
                )}
                className="relative flex h-[36px] w-[36px] items-center justify-center overflow-hidden rounded-full bg-gray-100"
            >
                {profileImage !== 'empty-image' && profileImage ? (
                    <img
                        src={profileImage}
                        alt={profileImage}
                        className="h-full w-full rounded-full object-cover object-center"
                    />
                ) : (
                    <User className="text-black" size={16} />
                )}
            </div>
            <ChevronDown
                strokeWidth={2}
                size={20}
                onClick={() => setMenuOpen((open) => !open)}
                className="cursor-pointer"
            />
            <AnimatePresence>
                {menuOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: -8, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -8, scale: 0.98 }}
                        transition={{ duration: 0.18 }}
                        className="absolute top-16 right-0 z-50 w-72 rounded-2xl border-gray-200 bg-white p-4 shadow-2xl"
                    >
                        {/* Profile section */}
                        <div className="flex flex-col px-3">

                            <div className="flex items-start gap-3">
                                <div className="relative flex h-14 w-14 items-center justify-center overflow-hidden rounded-full bg-gray-100">
                                    {profileImage !== 'empty-image' &&
                                        profileImage ? (
                                        <img
                                            src={profileImage}
                                            alt={profileImage}
                                            className="h-full w-full rounded-full object-cover object-center"
                                        />
                                    ) : (
                                        <User
                                            className="text-black"
                                            size={24}
                                        />
                                    )}
                                </div>
                                <div className="my-2 text-base">
                                    <p className="font-semibold text-black">
                                        {user?.displayName}
                                    </p>
                                    <p className="text-gray text-sm font-normal">
                                        {user?.email}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="mt-3 flex flex-col">


                            <div className="flex flex-col">
                                <div
                                    className="cursor-pointer rounded-md px-3 py-2 text-sm font-medium hover:bg-gray-100"
                                    onClick={() => {
                                        if (onSettingsClick) {
                                            onSettingsClick()
                                        } else {
                                            navigate({ to: ROUTES.SETTINGS })
                                        }
                                        setMenuOpen(false)
                                    }}
                                >
                                    Settings
                                </div>
                                <a
                                    href="https://chromewebstore.google.com/detail/what-the-f-are-you-wearin/pkmphplmpbcebmmddkckfckhejgcgjbi"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm font-medium hover:bg-gray-100"
                                    onClick={() => setMenuOpen(false)}
                                >
                                    Get the extension
                                    <img
                                        src={assetPath('icons/chrome.svg')}
                                        alt="Chrome"
                                        className="h-4 w-4"
                                    />
                                </a>
                                <div
                                    className="cursor-pointer rounded-md px-3 py-2 text-sm font-medium hover:bg-gray-100"
                                    onClick={
                                        isLoggingOut ? () => ({}) : handleLogout
                                    }
                                >
                                    {isLoggingOut
                                        ? 'Logging out...'
                                        : 'Log Out'}
                                </div>

                                {error && (
                                    <p className="mt-3 text-red-600">{error}</p>
                                )}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}
